import { useState } from "react";
import { Linkedin, Facebook, Instagram, Copy, Check, Image as ImageIcon, Loader2, ChevronDown, Calendar, Twitter, ExternalLink, Share2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { articlesApi } from "../lib/api";

const STYLES = [
  { id: "fotorrealista", label: "Fotorrealista" },
  { id: "3d", label: "3D" },
  { id: "ilustracion", label: "Ilustracion" },
  { id: "minimalista", label: "Minimalista" },
  { id: "corporativo", label: "Corporativo" },
  { id: "artistico", label: "Artistico" },
  { id: "geometrico", label: "Geometrico" },
];

const ENGINES = [
  { id: "openai", label: "GPT Image 1", desc: "Fotorrealista y detallado" },
  { id: "nano_banana", label: "Nano Banana", desc: "Creativo y artistico" },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text || "");
    setCopied(true);
    toast.success("Copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button data-testid="copy-social-btn" onClick={handleCopy}
      title="Copiar texto"
      className="p-1.5 rounded-md transition-colors duration-200"
      style={{ color: copied ? "#4ADE80" : "#94A3B8", background: "rgba(148,163,184,0.08)" }}>
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function DownloadTextButton({ text, filename }) {
  const handleDownload = () => {
    const blob = new Blob([text || ""], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "texto.txt";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 500);
  };
  return (
    <button data-testid="download-social-btn" onClick={handleDownload}
      title="Descargar texto (.txt)"
      className="p-1.5 rounded-md transition-colors duration-200"
      style={{ color: "#94A3B8", background: "rgba(148,163,184,0.08)" }}>
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
      </svg>
    </button>
  );
}

function NetworkActions({ text, network, articleId }) {
  return (
    <div className="flex gap-1">
      <CopyButton text={text} />
      <DownloadTextButton text={text} filename={`${network}-${(articleId || "").slice(0, 8)}.txt`} />
    </div>
  );
}

function LinkedInMockup({ post, articleId }) {
  return (
    <div data-testid="linkedin-preview" className="rounded-xl overflow-hidden" style={{ background: "#1B2838", border: "1px solid #1E3A5F" }}>
      <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid #1E3A5F" }}>
        <div className="flex items-center gap-2">
          <Linkedin className="w-5 h-5" style={{ color: "#0A66C2" }} />
          <span className="text-xs font-medium" style={{ color: "#94A3B8" }}>LinkedIn</span>
        </div>
        <NetworkActions text={post} network="linkedin" articleId={articleId} />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-10 h-10 rounded-full" style={{ background: "linear-gradient(135deg, #0A66C2, #004182)" }} />
          <div>
            <div className="text-sm font-medium" style={{ color: "#E2E8F0" }}>Alianza Empresarial</div>
            <div className="text-xs" style={{ color: "#94A3B8" }}>Plataforma B2B</div>
          </div>
        </div>
        <div className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: "#CBD5E1", fontFamily: "'Outfit'" }}>
          {post?.length > 400 ? post.slice(0, 400) + "... ver mas" : post}
        </div>
      </div>
    </div>
  );
}

function FacebookMockup({ post, articleId }) {
  return (
    <div data-testid="facebook-preview" className="rounded-xl overflow-hidden" style={{ background: "#1B2838", border: "1px solid #1E3A5F" }}>
      <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid #1E3A5F" }}>
        <div className="flex items-center gap-2">
          <Facebook className="w-5 h-5" style={{ color: "#1877F2" }} />
          <span className="text-xs font-medium" style={{ color: "#94A3B8" }}>Facebook</span>
        </div>
        <NetworkActions text={post} network="facebook" articleId={articleId} />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-10 h-10 rounded-full" style={{ background: "linear-gradient(135deg, #1877F2, #0D47A1)" }} />
          <div>
            <div className="text-sm font-medium" style={{ color: "#E2E8F0" }}>Alianza Empresarial</div>
            <div className="text-xs" style={{ color: "#94A3B8" }}>Hace 1 hora</div>
          </div>
        </div>
        <div className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: "#CBD5E1", fontFamily: "'Outfit'" }}>
          {post}
        </div>
      </div>
    </div>
  );
}

function InstagramMockup({ caption, hashtags, image, articleId }) {
  const fullText = `${caption || ""}\n\n${(hashtags || []).join(" ")}`;
  return (
    <div data-testid="instagram-preview" className="rounded-xl overflow-hidden" style={{ background: "#1B2838", border: "1px solid #1E3A5F" }}>
      <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid #1E3A5F" }}>
        <div className="flex items-center gap-2">
          <Instagram className="w-5 h-5" style={{ color: "#E1306C" }} />
          <span className="text-xs font-medium" style={{ color: "#94A3B8" }}>Instagram</span>
        </div>
        <NetworkActions text={fullText} network="instagram" articleId={articleId} />
      </div>
      {image ? (
        <div className="aspect-square overflow-hidden">
          <img src={`data:image/png;base64,${image}`} alt="Preview IG" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="aspect-square flex items-center justify-center" style={{ background: "linear-gradient(135deg, #1a365d, #2d4a7c)" }}>
          <div className="text-center px-6">
            <div className="text-lg font-bold mb-2" style={{ color: "#f97316", fontFamily: "'Space Grotesk'" }}>ALIANZA EMPRESARIAL</div>
            <div className="text-sm" style={{ color: "#E2E8F0", fontFamily: "'Outfit'" }}>Vista previa del post</div>
          </div>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-full" style={{ background: "linear-gradient(135deg, #E1306C, #F77737)" }} />
          <span className="text-xs font-medium" style={{ color: "#E2E8F0" }}>alianzaempresarial</span>
        </div>
        <div className="text-xs whitespace-pre-wrap leading-relaxed" style={{ color: "#CBD5E1" }}>
          {caption}
        </div>
        {hashtags && (
          <div className="mt-2 text-xs" style={{ color: "#0A66C2" }}>
            {hashtags.join(" ")}
          </div>
        )}
      </div>
    </div>
  );
}

function XMockup({ post, articleId }) {
  return (
    <div data-testid="x-preview" className="rounded-xl overflow-hidden" style={{ background: "#1B2838", border: "1px solid #1E3A5F" }}>
      <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid #1E3A5F" }}>
        <div className="flex items-center gap-2">
          <Twitter className="w-5 h-5" style={{ color: "#94A3B8" }} />
          <span className="text-xs font-medium" style={{ color: "#94A3B8" }}>X (Twitter)</span>
        </div>
        <NetworkActions text={post} network="x" articleId={articleId} />
      </div>
      <div className="p-4">
        <div className="flex items-start gap-2">
          <div className="w-9 h-9 rounded-full flex-shrink-0" style={{ background: "linear-gradient(135deg, #1a365d, #2d4a7c)" }} />
          <div className="flex-1">
            <div className="flex items-center gap-1 mb-1">
              <span className="text-xs font-bold" style={{ color: "#E2E8F0" }}>Alianza Empresarial</span>
              <span className="text-xs" style={{ color: "#64748B" }}>@alianzaemp</span>
            </div>
            <div className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: "#CBD5E1", fontFamily: "'Outfit'" }}>
              {post}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShareWPUrl({ wpLink }) {
  if (!wpLink) return null;
  const shareLinks = [
    { id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "#0A66C2",
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(wpLink)}` },
    { id: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2",
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(wpLink)}` },
    { id: "x", label: "X", icon: Twitter, color: "#94A3B8",
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(wpLink)}` },
  ];

  return (
    <div data-testid="share-wp-url" className="rounded-xl border p-4" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
      <h4 className="text-xs font-semibold mb-3 flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
        <Share2 className="w-3.5 h-3.5" style={{ color: "#00D4FF" }} />
        Compartir URL del articulo en redes
      </h4>
      <div className="flex gap-2">
        {shareLinks.map((s) => (
          <a key={s.id} data-testid={`share-wp-${s.id}`} href={s.url} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs transition-all duration-200 hover:opacity-80"
            style={{ borderColor: `${s.color}30`, color: s.color, background: `${s.color}08` }}>
            <s.icon className="w-3.5 h-3.5" />
            {s.label}
            <ExternalLink className="w-3 h-3" />
          </a>
        ))}
      </div>
      <p className="text-xs mt-2" style={{ color: "#64748B" }}>
        Abre cada red social para compartir la URL de tu articulo de WordPress y generar trafico.
      </p>
    </div>
  );
}

function SocialImageGenerator({ articleId }) {
  const [engine, setEngine] = useState("openai");
  const [style, setStyle] = useState("3d");
  const [network, setNetwork] = useState("instagram");
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [showOptions, setShowOptions] = useState(false);

  const pollStatus = async (jobId) => {
    for (let i = 0; i < 60; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await articlesApi.socialImageStatus(jobId);
        if (res.data.status === "completed") return res.data;
        if (res.data.status === "error") throw new Error(res.data.detail);
      } catch (e) {
        if (e.message) throw e;
      }
    }
    throw new Error("Timeout");
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await articlesApi.generateSocialImage({
        article_id: articleId, engine, style, network,
      });
      const result = await pollStatus(res.data.job_id);
      setGeneratedImage(result.image_base64);
      toast.success("Imagen generada correctamente");
    } catch (err) {
      toast.error(err.message || "Error al generar imagen");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="social-image-generator" className="rounded-xl border p-5" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
      <button onClick={() => setShowOptions(!showOptions)}
        className="w-full flex items-center justify-between text-left">
        <h4 className="text-sm font-semibold flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
          <ImageIcon className="w-4 h-4" style={{ color: "#00D4FF" }} />
          Generar Imagen para Redes
        </h4>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showOptions ? "rotate-180" : ""}`} style={{ color: "#94A3B8" }} />
      </button>

      {showOptions && (
        <div className="mt-4 space-y-4 animate-fade-in-up">
          {/* Engine selector */}
          <div>
            <label className="text-xs block mb-2" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>Motor de IA</label>
            <div className="grid grid-cols-2 gap-2">
              {ENGINES.map((e) => (
                <button key={e.id} data-testid={`engine-${e.id}`} onClick={() => setEngine(e.id)}
                  className="rounded-lg p-3 text-left transition-all duration-200 border"
                  style={{
                    background: engine === e.id ? "rgba(0,212,255,0.08)" : "#0B1622",
                    borderColor: engine === e.id ? "rgba(0,212,255,0.3)" : "#1E3A5F",
                  }}>
                  <div className="text-xs font-semibold" style={{ color: engine === e.id ? "#00D4FF" : "#E2E8F0" }}>{e.label}</div>
                  <div className="text-xs mt-0.5" style={{ color: "#94A3B8" }}>{e.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Style selector */}
          <div>
            <label className="text-xs block mb-2" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>Estilo visual</label>
            <div className="flex flex-wrap gap-1.5">
              {STYLES.map((s) => (
                <button key={s.id} data-testid={`style-${s.id}`} onClick={() => setStyle(s.id)}
                  className="text-xs px-3 py-1.5 rounded-full border transition-all duration-200"
                  style={{
                    background: style === s.id ? "rgba(0,212,255,0.12)" : "transparent",
                    borderColor: style === s.id ? "rgba(0,212,255,0.4)" : "#1E3A5F",
                    color: style === s.id ? "#00D4FF" : "#94A3B8",
                  }}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Network selector */}
          <div>
            <label className="text-xs block mb-2" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>Formato de red</label>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { id: "instagram", label: "Instagram (1:1)", icon: Instagram, color: "#E1306C" },
                { id: "facebook", label: "Facebook (16:9)", icon: Facebook, color: "#1877F2" },
                { id: "linkedin", label: "LinkedIn (16:9)", icon: Linkedin, color: "#0A66C2" },
                { id: "stories", label: "Stories (9:16)", icon: Instagram, color: "#F77737" },
              ].map((n) => (
                <button key={n.id} data-testid={`network-${n.id}`} onClick={() => setNetwork(n.id)}
                  className="flex items-center gap-2 rounded-lg p-2.5 text-xs border transition-all duration-200"
                  style={{
                    background: network === n.id ? "rgba(0,212,255,0.06)" : "#0B1622",
                    borderColor: network === n.id ? "rgba(0,212,255,0.3)" : "#1E3A5F",
                    color: network === n.id ? "#E2E8F0" : "#94A3B8",
                  }}>
                  <n.icon className="w-3.5 h-3.5" style={{ color: n.color }} />
                  {n.label}
                </button>
              ))}
            </div>
          </div>

          <Button data-testid="generate-social-image-btn" onClick={handleGenerate} disabled={generating}
            className="w-full rounded-full h-10 text-sm font-medium"
            style={{ background: generating ? "rgba(0,212,255,0.15)" : "linear-gradient(135deg, #00B4D8, #00D4FF)", color: generating ? "#00D4FF" : "#0B1622" }}>
            {generating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando imagen...</>
            ) : (
              <><ImageIcon className="w-4 h-4 mr-2" />Generar Imagen</>
            )}
          </Button>

          {generatedImage && (
            <div className="rounded-lg overflow-hidden border animate-fade-in-up" style={{ borderColor: "#1E3A5F" }}>
              <img src={`data:image/png;base64,${generatedImage}`} alt="Imagen social generada"
                className="w-full object-contain" style={{ maxHeight: "300px" }} />
              <div className="p-2 flex justify-end" style={{ background: "#0B1622" }}>
                <a href={`data:image/png;base64,${generatedImage}`} download={`social_${network}.png`}
                  className="text-xs px-3 py-1.5 rounded-full" style={{ background: "rgba(0,212,255,0.1)", color: "#00D4FF" }}>
                  Descargar
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SocialPreviews({ socialPosts, articleId, wpLink, readOnly, activeNetworks }) {
  if (!socialPosts) return null;
  // activeNetworks example: { linkedin: true, facebook: false, instagram: true, x: true }
  // If not provided, show all (backwards compatible)
  const show = (net) => !activeNetworks || activeNetworks[net] !== false;

  return (
    <div data-testid="social-previews" className="space-y-4">
      <h3 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk'", color: "#E2E8F0" }}>
        Contenido para Redes Sociales
      </h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {show("linkedin") && socialPosts.linkedin_post && <LinkedInMockup post={socialPosts.linkedin_post} articleId={articleId} />}
        {show("facebook") && socialPosts.facebook_post && <FacebookMockup post={socialPosts.facebook_post} articleId={articleId} />}
        {show("instagram") && (socialPosts.instagram_caption || socialPosts.hashtags_instagram) && (
          <InstagramMockup caption={socialPosts.instagram_caption} hashtags={socialPosts.hashtags_instagram} articleId={articleId} />
        )}
        {show("x") && socialPosts.x_post && <XMockup post={socialPosts.x_post} articleId={articleId} />}
      </div>
      {wpLink && <ShareWPUrl wpLink={wpLink} />}
    </div>
  );
}
